/**
 * Scenario Comparison Page - Side-by-side scenario analysis
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitCompare, ArrowLeft, Loader2, CheckCircle2, XCircle,
  AlertTriangle, TrendingDown, DollarSign, Shield, Target,
  Award, ThumbsUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


function fmt$(v) { return v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v/1e6).toFixed(0)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`; }
function fmtE(v) { return v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : `${v}`; }

export default function ScenarioComparePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scenarios, setScenarios] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    apiFetch(`/v1/simulations`)
      .then(d => {
        if (!d || d._error) return;
        const simulated = (d.scenarios || []).filter(s => s.status === 'simulated' || s.status === 'committed');
        setScenarios(simulated);
        // Auto-select first 2 if available
        if (simulated.length >= 2) {
          setSelected([simulated[0].scenarioId, simulated[1].scenarioId]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setComparison(null);
  };

  const compare = async () => {
    if (selected.length < 2) return;
    setComparing(true);
    try {
      const res = await apiFetch(`/v1/simulations/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarioIds: selected }),
      });
      if (res && !res._error) setComparison(res);
    } catch {
      toast({ title: 'Comparison failed', variant: 'destructive' });
    } finally {
      setComparing(false);
    }
  };

  const rec = comparison?.recommendation;

  return (
    <div className="space-y-6" data-testid="scenario-compare-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/simulations')} className="text-[#6B6B6B] hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-violet-400" />
              Compare Scenarios
            </h1>
            <p className="text-xs text-[#6B6B6B]">Select 2+ simulated scenarios to compare side-by-side</p>
          </div>
        </div>
        <Button onClick={compare} disabled={selected.length < 2 || comparing} className="bg-violet-600 hover:bg-violet-700" data-testid="compare-btn">
          {comparing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <GitCompare className="w-4 h-4 mr-1" />}
          Compare ({selected.length})
        </Button>
      </div>

      {/* Scenario Selection */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>
      ) : scenarios.length < 2 ? (
        <div className="text-center py-12 text-[#6B6B6B]">
          <GitCompare className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p>Need at least 2 simulated scenarios to compare.</p>
          <Button variant="outline" className="mt-3 border-[#2F2F2F]" onClick={() => navigate('/simulations')}>Go to Scenarios</Button>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          {scenarios.map(s => (
            <button
              key={s.scenarioId}
              onClick={() => toggleSelect(s.scenarioId)}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                selected.includes(s.scenarioId)
                  ? 'bg-violet-500/20 text-violet-400 border-violet-500/30'
                  : 'bg-[#111111] text-[#A0A0A0] border-[#1F1F1F] hover:border-[#2F2F2F]'
              }`}
              data-testid={`select-${s.scenarioId}`}
            >
              {s.scenarioName}
            </button>
          ))}
        </div>
      )}

      {/* Comparison Table */}
      {comparison && (
        <>
          {/* Recommendation Banner */}
          {rec && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3" data-testid="recommendation-banner">
              <Award className="w-6 h-6 text-emerald-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-400">
                  Recommended: {rec.scenarioName}
                </p>
                <p className="text-xs text-[#A0A0A0]">Score: {rec.score}/100</p>
              </div>
            </div>
          )}

          {/* Comparison Grid */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="py-3 px-4 text-left text-xs font-medium text-[#6B6B6B] w-48">Metric</th>
                  {comparison.scenarios.map(s => (
                    <th key={s.scenarioId} className="py-3 px-4 text-center text-xs font-medium text-white">
                      <div className="flex items-center justify-center gap-1">
                        {s.scenarioName}
                        {rec?.scenarioId === s.scenarioId && <ThumbsUp className="w-3 h-3 text-emerald-400" />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F1F1F]">
                <CompRow label="Net Zero?" icon={Target}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center">
                      {s.achievesNetZero ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />}
                    </td>
                  ))}
                </CompRow>
                <CompRow label="Emissions Reduction" icon={TrendingDown}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm font-bold text-white">{s.percentageReduction}%</td>
                  ))}
                </CompRow>
                <CompRow label="Net Emissions" icon={TrendingDown}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm text-[#A0A0A0]">{fmtE(s.netEmissions)} tCO2e</td>
                  ))}
                </CompRow>
                <CompRow label="Total Cost" icon={DollarSign}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm font-bold text-white">{fmt$(s.totalCost)}</td>
                  ))}
                </CompRow>
                <CompRow label="Cost/Ton Avoided" icon={DollarSign}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm text-[#A0A0A0]">{fmt$(s.costPerTonAvoided)}/tCO2e</td>
                  ))}
                </CompRow>
                <CompRow label="CSRD Score" icon={Shield}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm font-bold text-white">{s.csrdScore}/100</td>
                  ))}
                </CompRow>
                <CompRow label="CDP Rating" icon={Shield}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm text-[#A0A0A0]">{s.cdpRating}</td>
                  ))}
                </CompRow>
                <CompRow label="Risk" icon={AlertTriangle}>
                  {comparison.scenarios.map(s => {
                    const c = s.overallRisk === 'low' ? 'text-emerald-400' : s.overallRisk === 'medium' ? 'text-amber-400' : 'text-red-400';
                    return <td key={s.scenarioId} className={`py-3 px-4 text-center text-sm font-medium ${c}`}>{s.overallRisk}</td>;
                  })}
                </CompRow>
                <CompRow label="Success Probability" icon={Target}>
                  {comparison.scenarios.map(s => (
                    <td key={s.scenarioId} className="py-3 px-4 text-center text-sm text-[#A0A0A0]">{Math.round(s.probabilityOfSuccess * 100)}%</td>
                  ))}
                </CompRow>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CompRow({ label, icon: Icon, children }) {
  return (
    <tr className="hover:bg-[#0D0D0D]">
      <td className="py-3 px-4 text-xs text-[#6B6B6B] flex items-center gap-2">
        <Icon className="w-3 h-3" />{label}
      </td>
      {children}
    </tr>
  );
}
